import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlReadRaw as sqlRead, turnoverWrite as sqlWrite, payloadOf } from "../../utils/sqlClient";
import { OPTION_LISTS, LOCKED_LISTS, effectiveOptions, nextOrder, renumber, optionPayload, listDef } from "../../config/turnoverOptions";

// Turnover Configuration, right column: the turnover's editable dropdown
// lists (TurnoverConfigOptions, db/28). Built-ins come from code; this stores
// only renames, order, retirements and added options.
const ctl = { padding: "5px 8px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", boxSizing: "border-box" };
const iconBtn = { padding: "2px 6px", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 5, background: "var(--surface)", color: "var(--text2)", cursor: "pointer" };

export default function DropdownListsPanel() {
  const { accounts } = useMsal();
  const user = accounts?.[0];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [listKey, setListKey] = useState(OPTION_LISTS[0].key);
  const [showRetired, setShowRetired] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    return sqlRead("EXEC sp_GetTurnoverConfigOptions")
      .then(r => { setRows(r?.Table1 || []); setError(""); })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  async function save(patch) {
    setBusy(true);
    try {
      const res = await sqlWrite(`EXEC sp_SaveTurnoverConfigOption @payload = '${payloadOf(optionPayload(listKey, patch, user))}'`);
      const out = res?.ResultSets?.Table1?.[0];
      if (!out || Number(out.Result) !== 1) throw new Error(out?.Error || "The change was not saved.");
      setRows(prev => [...prev.filter(r => r.OptionID !== out.OptionID), out]);
    } catch (e) {
      alert("Error saving: " + e.message);
      load();
    } finally {
      setBusy(false);
    }
  }

  const def = listDef(listKey);
  const options = effectiveOptions(listKey, rows, { includeRetired: true });
  const visible = showRetired ? options : options.filter(o => o.active);
  const retiredCount = options.length - options.filter(o => o.active).length;
  const labelLocked = (o) => o.locked === "Built-in";

  // Move one place in the full (retired-included) order, renumbering the list.
  async function move(o, dir) {
    const i = options.findIndex(x => x.key === o.key);
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const seq = [...options];
    [seq[i], seq[j]] = [seq[j], seq[i]];
    for (const c of renumber(seq)) await save({ option_key: c.key, label: c.label, display_order: c.order });
  }

  async function addOption() {
    const label = newLabel.trim();
    if (!label) return;
    if (options.some(o => o.label.toLowerCase() === label.toLowerCase())) { alert(`"${label}" is already on this list.`); return; }
    await save({ label, display_order: nextOrder(listKey, rows) });
    setNewLabel("");
  }

  const groups = [...new Set(OPTION_LISTS.map(l => l.group))];

  return (
    <div>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", margin: "0 0 4px" }}>Dropdown lists</h3>
      <p style={{ fontSize: 12, color: "var(--text3)", margin: "0 0 12px" }}>
        Rename, reorder, retire or add the choices offered in the turnover. Retiring hides an option from new choices;
        records that already use it keep it.
      </p>

      {loading ? <div style={{ fontSize: 12.5, color: "var(--text3)" }}>Loading…</div>
        : error ? <div style={{ fontSize: 12.5, color: "var(--red)" }}>{error}</div>
        : (
          <>
            <select value={listKey} onChange={e => { setListKey(e.target.value); setNewLabel(""); }} style={{ ...ctl, width: "100%", marginBottom: 8 }} aria-label="List">
              {groups.map(g => (
                <optgroup key={g} label={g}>
                  {OPTION_LISTS.filter(l => l.group === g).map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                </optgroup>
              ))}
            </select>
            {def?.help && <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 8 }}>{def.help}</div>}

            <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: 8, background: "var(--surface)" }}>
              {visible.length === 0 && <div style={{ fontSize: 12, color: "var(--text3)", padding: 4 }}>No options yet.</div>}
              {visible.map(o => (
                <div key={o.key} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 6, alignItems: "center", marginBottom: 5, opacity: o.active ? 1 : 0.55 }}>
                  {labelLocked(o)
                    ? <div style={{ fontSize: 12.5, color: "var(--text)", padding: "5px 2px" }} title="Built-in — its name is used by checks and folders">🔒 {o.label}</div>
                    : <input key={`${o.key}-${o.label}`} defaultValue={o.label} aria-label="Option name" style={{ ...ctl, width: "100%" }}
                        onBlur={e => { const v = e.target.value.trim(); if (v && v !== o.label) save({ option_key: o.key, label: v, display_order: o.order }); else e.target.value = o.label; }} />}
                  <div style={{ display: "flex", gap: 3 }}>
                    <button style={iconBtn} disabled={busy} title="Move up" onClick={() => move(o, -1)}>↑</button>
                    <button style={iconBtn} disabled={busy} title="Move down" onClick={() => move(o, 1)}>↓</button>
                    {o.locked === "Always required" || labelLocked(o)
                      ? <span style={{ ...iconBtn, cursor: "default", color: "var(--text3)" }} title={o.locked === "Always required" ? "Always required — can't be retired" : "Built-in — can't be retired"}>—</span>
                      : <button style={iconBtn} disabled={busy} title={o.active ? "Retire" : "Restore"}
                          onClick={() => save({ option_key: o.key, label: o.label, display_order: o.order, is_active: !o.active })}>{o.active ? "Retire" : "Restore"}</button>}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === "Enter" && addOption()}
                  placeholder="New option" aria-label="New option" style={{ ...ctl, flex: 1 }} />
                <button onClick={addOption} disabled={busy || !newLabel.trim()}
                  style={{ padding: "5px 12px", fontSize: 12, fontWeight: 700, background: "var(--surface)", color: "var(--cs-blue)", border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer" }}>+ Add</button>
              </div>
              {retiredCount > 0 && (
                <label style={{ display: "block", fontSize: 11.5, color: "var(--text3)", marginTop: 8 }}>
                  <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} /> Show {retiredCount} retired
                </label>
              )}
            </div>
          </>
        )}

      <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", margin: "24px 0 4px" }}>Fixed lists</h3>
      <p style={{ fontSize: 12, color: "var(--text3)", margin: "0 0 8px" }}>These drive the workflow, so they are not editable here.</p>
      {LOCKED_LISTS.map(l => (
        <div key={l.label} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>🔒 {l.label}</div>
          <div style={{ fontSize: 11.5, color: "var(--text2)" }}>{l.values.join(" · ")}</div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>{l.reason}</div>
        </div>
      ))}
    </div>
  );
}
